import type { InteractionEvent } from '../../interaction/types';
import { ObliqueSliceViewport2D } from '../../viewports/oblique-slice-viewport-2d';
import { SliceViewport2D } from '../../viewports/slice-viewport-2d';
import type { IViewport } from '../../viewports/types';
import type { ITool } from '../types';

/**
 * Slice scroll tool — changes the current slice in 2D viewports via scroll wheel.
 *
 * Uses the SliceViewport2D index-based API so each scroll tick advances
 * exactly one voxel (orthogonal viewports) or one centerline node (oblique).
 *
 * Location service synchronization is handled inside setSliceIndex():
 * - Non-oblique viewports broadcast their world position directly.
 * - ObliqueSliceViewport2D publishes the centerline position when the index changes.
 *
 * Orthogonal viewports:
 *   Scroll up → advance (higher index)
 *   Scroll down → retreat (lower index)
 *
 * Oblique viewports (centerline-based):
 *   Scroll down → advance (higher index, distal along vessel)
 *   Scroll up → retreat (lower index, proximal along vessel)
 */
export class SliceScrollTool implements ITool {
    static readonly toolName = 'slice-scroll';

    readonly id = SliceScrollTool.toolName;
    readonly name = 'Slice Scroll';
    readonly cursor = 'default';
    readonly completed = false;

    private viewport: IViewport | null = null;

    activate(viewport: IViewport): void {
        this.viewport = viewport;
    }

    deactivate(): void {
        this.viewport = null;
    }

    cancel(): void {
        // Nothing to cancel for wheel-only tool
    }

    handleEvent(event: InteractionEvent): boolean {
        if (event.type !== 'wheel' || event.deltaY === undefined) {
            return false;
        }

        if (!(this.viewport instanceof SliceViewport2D)) {
            return false;
        }

        // Oblique viewports follow centerline node order (proximal → distal),
        // so scroll-down should advance along the vessel (increase index).
        // Orthogonal viewports use the traditional inverted mapping.
        const isOblique = this.viewport instanceof ObliqueSliceViewport2D;
        const direction = event.deltaY > 0 ? (isOblique ? 1 : -1) : isOblique ? -1 : 1;
        this.viewport.setSliceIndex(this.viewport.sliceIndex + direction);
        return true;
    }
}
